import React from 'react';
import { SentenceApi } from '../api/SentenceApi';
import { IVocabularyWord } from '../api/VocabularyWordApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import { answerPromptPinyin } from '../component/studentInstructionalPinyin';
import { pinyinForEditing } from './pinyinForEditing';
import { usePinyinRequests } from './pinyinRequests';

export type TextFieldKey = keyof Pick<IVocabularyWord,
    'word' |
    'audio_sentence' |
    'teacher_chinese_prompt' |
    'student_response_prompt'
>;

export type PinyinFieldKey = keyof Pick<IVocabularyWord,
    'word_pinyin' |
    'audio_sentence_pinyin' |
    'teacher_chinese_prompt_pinyin' |
    'student_response_prompt_pinyin'
>;

export const textToPinyinMap: Record<TextFieldKey, PinyinFieldKey> = {
    word: 'word_pinyin',
    audio_sentence: 'audio_sentence_pinyin',
    teacher_chinese_prompt: 'teacher_chinese_prompt_pinyin',
    student_response_prompt: 'student_response_prompt_pinyin'
};

/** A field's saved reading, for opening it. A response prompt's comes from its saved sentence (see answerPromptPinyin). */
export function savedVocabularyPinyin(word: IVocabularyWord, field: TextFieldKey) {
    const saved = word[textToPinyinMap[field]];
    return field === 'student_response_prompt' && saved?.length ? answerPromptPinyin(word[field] ?? '', word.word ?? '', saved) : saved;
}

interface UseVocabularyWordPinyinProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
    initialWord?: IVocabularyWord;
}

export const initialVocabularyWord: IVocabularyWord = {
    id: 0,
    word: '',
    word_pinyin: [],
    image: '',
    image_url: '',
    audio_sentence: '',
    audio_sentence_pinyin: [],
    audio_sentence_id: 0,
    dialog_prompt_id: 0,
    teacher_english_prompt: '',
    teacher_chinese_prompt: '',
    teacher_chinese_prompt_pinyin: [],
    teacher_chinese_prompt_sentence_id: 0,
    student_response_prompt: '<Answer/>',
    student_response_prompt_pinyin: ['<Answer/>'],
    student_response_prompt_sentence_id: 0,
    word_info_text: null,
    english_text: null
};


export function useVocabularyWordPinyin({ history, location, setErrorMessage, initialWord = initialVocabularyWord }: UseVocabularyWordPinyinProps) {
    const [vocabularyWord, setVocabularyWord] = React.useState<IVocabularyWord>(initialWord);
    const [vocabularyWordPinyinMap, setVocabularyWordPinyinMap] = React.useState<Record<PinyinFieldKey, string>>(() => {
        return Object.fromEntries(
            (Object.values(textToPinyinMap) as PinyinFieldKey[]).map(k => [k, ''])
        ) as Record<PinyinFieldKey, string>;
    });
    const [vocabularyWordErrorMap, setVocabularyWordErrorMap] = React.useState<Record<string, string | null>>({});
    const suggestions = usePinyinRequests<PinyinFieldKey>();
    const validations = usePinyinRequests<PinyinFieldKey>();
    const latestWord = React.useRef(vocabularyWord);
    latestWord.current = vocabularyWord;

    const setVocabularyWordValue = React.useCallback((key: keyof IVocabularyWord, value: any) => {
        setVocabularyWord(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const setVocabularyWordPinyin = React.useCallback((pinyinField: PinyinFieldKey, pinyinList: string[]) => {
        setVocabularyWord(prev => ({
            ...prev,
            [pinyinField]: pinyinList
        }));
        setVocabularyWordPinyinMap(prev => ({
            ...prev,
            [pinyinField]: pinyinList.join(' ')
        }));
        setVocabularyWordErrorMap(prev => ({
            ...prev,
            [pinyinField]: null
        }));
    }, []);

    const clearVocabularyWordFields = React.useCallback(() => {
        suggestions.cancelAll();
        validations.cancelAll();
        setVocabularyWord(initialVocabularyWord)
        setVocabularyWordErrorMap({});
    }, [suggestions, validations]);

    const clearVocabularyWordErrors = React.useCallback(() => {
        setVocabularyWordErrorMap({});
    }, []);

    const fetchNumericPinyinForVocabularyField = React.useCallback((updatedWord: IVocabularyWord, field: TextFieldKey) => {
        const originalText = updatedWord[field] || '';
        if (!originalText.trim()) return;

        const pinyinField = textToPinyinMap[field];
        const isLatest = suggestions.start(pinyinField);
        pinyinForEditing(originalText, savedVocabularyPinyin(updatedWord, field))
            .then((pinyinList) => {
                if (isLatest()) setVocabularyWordPinyin(pinyinField, pinyinList);
            })
            .catch((err) => {
                if (!isLatest()) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setVocabularyWordErrorMap(prev => ({
                    ...prev,
                    [pinyinField]: 'Unable to fetch pinyin'
                }));
            });
    }, [setVocabularyWordPinyin, suggestions, setErrorMessage, history, location]);

    const regenerateStudentResponsePromptPinyin = React.useCallback(() => {
        const { student_response_prompt: text, student_response_prompt_pinyin: reading } = latestWord.current;
        // The prompt's reading does not depend on the word's: only a prompt without one, as it is now, is given a suggestion.
        if (text && text.trim() && !reading?.length) {
            const isLatest = suggestions.start('student_response_prompt_pinyin');
            SentenceApi.getTextPinyin(text)
                .then((response) => {
                    if (isLatest()) setVocabularyWordPinyin('student_response_prompt_pinyin', response.data.flat());
                })
                .catch((err) => {
                    if (!isLatest()) return;
                    handleErrorMessage(err, setErrorMessage, history, location);
                    setVocabularyWordErrorMap(prev => ({
                        ...prev,
                        student_response_prompt_pinyin: 'Unable to fetch pinyin'
                    }));
                });
        }
    }, [setVocabularyWordPinyin, suggestions, setErrorMessage, history, location]);

    const validatePinyin = React.useCallback((vocabularyWord: IVocabularyWord, pinyinField: PinyinFieldKey, pinyinList: string[]) => {
        const originalField = (Object.entries(textToPinyinMap).find(([_, v]) => v === pinyinField) || [])[0] as TextFieldKey;
        const originalText = vocabularyWord[originalField] || '';
        const isLatest = validations.start(pinyinField);

        SentenceApi.isTextPinyinValid(originalText, pinyinList, true)
            .then((response) => {
                if (!isLatest()) return;
                const isValid = response.data;
                setVocabularyWordErrorMap(prev => ({
                    ...prev,
                    [pinyinField]: isValid ? null : 'Pinyin invalid or incorrect length'
                }));
            })
            .catch((err) => {
                if (!isLatest()) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setVocabularyWordErrorMap(prev => ({
                    ...prev,
                    [pinyinField]: 'Unable to validate pinyin'
                }));
            });
    }, [validations, setErrorMessage, history, location]);

    const setVocabularyWordFields = React.useCallback((updatedWord: IVocabularyWord) => {
        suggestions.cancelAll();
        validations.cancelAll();
        setVocabularyWord(updatedWord);

        const newPinyin: Record<PinyinFieldKey, string> = {} as Record<PinyinFieldKey, string>;

        (Object.keys(textToPinyinMap) as TextFieldKey[]).forEach((field) => {
            const pinyinField = textToPinyinMap[field];
            const originalText = updatedWord[field] || '';

            if (originalText.trim()) {
                newPinyin[pinyinField] = '';
                fetchNumericPinyinForVocabularyField(updatedWord, field);
            } else {
                newPinyin[pinyinField] = '';
            }
        });

        setVocabularyWordPinyinMap(newPinyin);
    }, [fetchNumericPinyinForVocabularyField, suggestions, validations]);

    const handleVocabularyWordTextChange = React.useCallback((field: keyof IVocabularyWord, value: string) => {
        setVocabularyWord(prev => ({
            ...prev,
            [field]: value
        }));

        if (Object.prototype.hasOwnProperty.call(textToPinyinMap, field)) {
            const textField = field as TextFieldKey;
            // A reading belongs to its text: changed text has none until its suggestion comes, so none can be saved with it.
            setVocabularyWord(prev => ({ ...prev, [textToPinyinMap[textField]]: [] }));
            setVocabularyWordPinyinMap(prev => ({ ...prev, [textToPinyinMap[textField]]: '' }));
            const isLatest = suggestions.start(textToPinyinMap[textField]);
            validations.start(textToPinyinMap[textField]);
            SentenceApi.getTextPinyin(value)
                .then((response) => {
                    if (!isLatest()) return;
                    const pinyinList = response.data.flat();
                    setVocabularyWordPinyin(textToPinyinMap[textField], pinyinList);
                    if (field === 'word') regenerateStudentResponsePromptPinyin();
                })
                .catch((err) => {
                    if (!isLatest()) return;
                    handleErrorMessage(err, setErrorMessage, history, location);
                    setVocabularyWordErrorMap(prev => ({
                        ...prev,
                        [textToPinyinMap[textField]]: 'Unable to fetch pinyin'
                    }));
                });
        }
    }, [setVocabularyWordPinyin, regenerateStudentResponsePromptPinyin, suggestions, validations, setErrorMessage, history, location]);

    const handleVocabularyWordPinyinChange = React.useCallback((pinyinField: PinyinFieldKey, value: string) => {
        const pinyinList = value.trim().split(/\s+/).filter(Boolean);
        suggestions.start(pinyinField);
        setVocabularyWord(prev => ({
            ...prev,
            [pinyinField]: pinyinList
        }));
        setVocabularyWordPinyinMap(prev => ({
            ...prev,
            [pinyinField]: value
        }));
        validatePinyin(vocabularyWord, pinyinField, pinyinList)
    }, [validatePinyin, suggestions, vocabularyWord]);

    return {
        vocabularyWord,
        vocabularyWordPinyinMap,
        vocabularyWordErrorMap,
        setVocabularyWordValue,
        setVocabularyWordFields,
        clearVocabularyWordFields,
        clearVocabularyWordErrors,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    };
}
